/**
 * Chat Attachment Validation (Frontend)
 *
 * Chat-specific file limits (stricter than library uploads).
 * Mirrors backend chat-attachment-limits.ts.
 */

import { formatFileSize } from '@/lib/schemas'

// ===================
// Constants
// ===================

export const MAX_CHAT_ATTACHMENTS = 5
export const MAX_CHAT_TOTAL_SIZE = 25 * 1024 * 1024 // 25MB

/** Chat-specific size limits per MIME type */
const CHAT_SIZE_LIMITS: Record<string, number> = {
  // PDF
  'application/pdf': 10 * 1024 * 1024,
  // Word
  'application/msword': 10 * 1024 * 1024,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 10 * 1024 * 1024,
  // Excel
  'application/vnd.ms-excel': 5 * 1024 * 1024,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 5 * 1024 * 1024,
  // CSV
  'text/csv': 5 * 1024 * 1024,
  // Text
  'text/plain': 1 * 1024 * 1024,
  // Images
  'image/png': 5 * 1024 * 1024,
  'image/jpeg': 5 * 1024 * 1024,
}

/** Accepted file types for file input / dropzone */
export const CHAT_ACCEPTED_FILE_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
}

// ===================
// Validation
// ===================

/** Types deliberately turned away with a specific reason rather than the generic message. */
const UNSUPPORTED_TYPE_MESSAGES: Record<string, string> = {
  'application/vnd.ms-powerpoint': "PowerPoint files aren't supported yet",
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    "PowerPoint files aren't supported yet",
}

/**
 * Metadata-only validation — the same rules as {@link validateChatAttachment}
 * without needing a `File`. SharePoint items are picked by name/mimeType/size,
 * so there is no local file to inspect.
 */
export function validateChatAttachmentMeta(
  filename: string,
  mimeType: string,
  size: number
): { valid: boolean; error?: string } {
  const maxSize = CHAT_SIZE_LIMITS[mimeType]
  if (maxSize === undefined) {
    const specific = UNSUPPORTED_TYPE_MESSAGES[mimeType]
    if (specific) {
      return { valid: false, error: specific }
    }
    return { valid: false, error: `File type '${mimeType || 'unknown'}' is not supported for chat attachments` }
  }

  if (size > maxSize) {
    return {
      valid: false,
      error: `${filename} (${formatFileSize(size)}) exceeds the ${formatFileSize(maxSize)} limit`,
    }
  }

  return { valid: true }
}

export function validateChatAttachment(file: File): { valid: boolean; error?: string } {
  return validateChatAttachmentMeta(file.name, file.type, file.size)
}

/**
 * Picker-facing reason a SharePoint item can't be attached in chat, or null
 * when it can. Surface-neutral copy — the Assistant chat has no Documents
 * tab, so the redirect names both destinations.
 */
export function describeChatAttachmentIneligibility(
  mimeType: string | null,
  size: number
): string | null {
  if (!mimeType) return "This file type can't be attached in chat"
  if (UNSUPPORTED_TYPE_MESSAGES[mimeType]) return "PowerPoint files can't be attached in chat"

  const maxSize = CHAT_SIZE_LIMITS[mimeType]
  if (maxSize === undefined) return "This file type can't be attached in chat"

  if (size > maxSize) {
    return `Too large to attach in chat (max ${formatFileSize(maxSize)}) — import it to your Library or the bid's documents instead`
  }

  return null
}
